import React, { useMemo, useState } from 'react';
import { Box, Button, FormControlLabel, Paper, Switch, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { DEEP_GREEN, MUTED } from '../colorTokens';
import { GATE_COLOR, areaGrade, formatNumber, formatPercent } from './format';

const MAX_RELEASES = 10;
const MIN_RELEASES_PRESENT = 2;
const CELL_W = 52;
const LABEL_W = 260;
const PRODUCT_LABEL = { legacy: 'Legacy', modern: 'Modern', other: 'Other' };
const GRADE_RANK = { hold: 0, caution: 1, go: 2, na: 3 };

const rowKey = (a) => `${a.product}|${a.area.toLowerCase()}`;

const Legend = () => (
  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
    {[['go', 'Passing'], ['caution', 'Minor gaps'], ['hold', 'Gaps open'], ['na', 'Nothing run']].map(([grade, label]) => (
      <Box key={grade} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: grade === 'na' ? '#e3e7ea' : GATE_COLOR[grade] }} />
        <Typography variant="caption" sx={{ color: MUTED }}>{label}</Typography>
      </Box>
    ))}
  </Box>
);

const Cell = ({ area, version, cell }) => {
  const grade = cell ? areaGrade(cell) : null;
  const title = cell
    ? `${area.area} in ${version}: ${formatNumber(cell.passed)} passed, ${formatNumber(cell.failed)} failed, ${formatNumber(cell.blocked + cell.notExecuted)} not yet proven. ${cell.passed + cell.failed ? `${formatPercent(cell.executedPassRate)} of what was run passed.` : 'Nothing was run.'}`
    : `${area.area} had no tests in ${version}.`;
  return (
    <Tooltip title={title} placement="top" enterDelay={150}>
      <Box sx={{ height: 24, p: '2px' }}>
        <Box sx={{ width: '100%', height: '100%', borderRadius: '4px', ...(cell ? { bgcolor: grade === 'na' ? '#e3e7ea' : GATE_COLOR[grade] } : { border: '1px dashed rgba(0,0,0,0.12)' }) }} />
      </Box>
    </Tooltip>
  );
};

const AreaHeatmap = ({ releases, onSelect }) => {
  const [product, setProduct] = useState('all');
  const [gapsOnly, setGapsOnly] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const columns = useMemo(
    () => releases.filter((r) => r.stage === 'released' && r.tests && r.tests.byArea.length).slice(0, MAX_RELEASES).reverse(),
    [releases]
  );

  const rows = useMemo(() => {
    const map = new Map();
    for (const col of columns) {
      for (const a of col.tests.byArea) {
        const key = rowKey(a);
        if (!map.has(key)) map.set(key, { key, product: a.product, area: a.area, cells: new Map(), total: 0 });
        const row = map.get(key);
        row.cells.set(col.version, a);
        row.total += a.total;
      }
    }
    const latest = columns[columns.length - 1];
    const list = [...map.values()].map((row) => ({ ...row, present: row.cells.size, latest: latest ? areaGrade(row.cells.get(latest.version)) : 'na' }));
    return list
      .filter((row) => product === 'all' || row.product === product)
      .filter((row) => !gapsOnly || [...row.cells.values()].some((c) => ['hold', 'caution'].includes(areaGrade(c))))
      .sort((a, b) => GRADE_RANK[a.latest] - GRADE_RANK[b.latest] || b.total - a.total);
  }, [columns, product, gapsOnly]);

  if (!columns.length) return null;
  const minPresent = Math.min(MIN_RELEASES_PRESENT, columns.length);
  const recurring = rows.filter((row) => row.present >= minPresent);
  const oneOff = rows.length - recurring.length;
  const shown = expanded ? rows : recurring;

  return (
    <Paper variant="outlined" sx={{ borderRadius: '12px', borderColor: 'rgba(0,0,0,0.05)', p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ fontWeight: 600, color: DEEP_GREEN }}>Where the testing effort goes</Typography>
          <Typography variant="body2" sx={{ color: MUTED, maxWidth: 680 }}>
            Each square is one product area in one release, coloured by how much of what was run passed. Areas that recur across releases are shown first, with open gaps in the latest release on top. Earlier maintenance plans only ran smoke and regression checks, so their other areas are empty.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <ToggleButtonGroup size="small" exclusive value={product} onChange={(_, v) => v && setProduct(v)} sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, py: 0.25 } }}>
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="legacy">Legacy</ToggleButton>
            <ToggleButton value="modern">Modern</ToggleButton>
          </ToggleButtonGroup>
          <FormControlLabel control={<Switch size="small" checked={gapsOnly} onChange={(e) => setGapsOnly(e.target.checked)} color="success" />} label={<Typography variant="body2">Only areas with gaps</Typography>} sx={{ mr: 0 }} />
        </Box>
      </Box>
      <Box sx={{ mt: 1.5 }}><Legend /></Box>
      <Box sx={{ mt: 1.5, overflowX: 'auto' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: `${LABEL_W}px repeat(${columns.length}, ${CELL_W}px)`, width: 'max-content', alignItems: 'center' }}>
          <Box />
          {columns.map((c) => (
            <Box key={c.version} onClick={() => onSelect(c.version)} sx={{ textAlign: 'center', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, color: DEEP_GREEN, pb: 0.5, '&:hover': { textDecoration: 'underline' } }}>
              {c.version}
            </Box>
          ))}
          {shown.map((row) => (
            <React.Fragment key={row.key}>
              <Box sx={{ pr: 1.5, overflow: 'hidden' }}>
                <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 500 }} title={row.area}>{row.area}</Typography>
                <Typography noWrap variant="caption" sx={{ color: MUTED, display: 'block', lineHeight: 1.1 }}>{PRODUCT_LABEL[row.product]}</Typography>
              </Box>
              {columns.map((c) => <Cell key={c.version} area={row} version={c.version} cell={row.cells.get(c.version)} />)}
            </React.Fragment>
          ))}
        </Box>
        {!rows.length && <Typography variant="body2" sx={{ color: MUTED, py: 2 }}>No areas match.</Typography>}
      </Box>
      {oneOff > 0 && (
        <Button size="small" onClick={() => setExpanded((v) => !v)} sx={{ mt: 1, textTransform: 'none', color: DEEP_GREEN }}>
          {expanded ? `Hide ${oneOff} areas seen in only one release` : `Show ${oneOff} more areas seen in only one release`}
        </Button>
      )}
    </Paper>
  );
};

export default AreaHeatmap;
